/**
 * ConnectWise (CW) read-only connector contract.
 *
 * Catalyst treats ConnectWise Manage as a **read-only** source: the daily sync pulls
 * time entries from CW and mirrors them into a local staging table, but Catalyst
 * never writes back to ConnectWise. The mirror is the migration bridge during
 * cutover — imported rows are reconciled to native Catalyst time entries later
 * (Phase 7), so this contract intentionally models the *staged* shape, not the
 * native `TimeEntry`.
 *
 * Auth uses CW's static member API keys (company + public/private key + clientId),
 * so there is no OAuth dance: the connector is simply *configured* or not, and every
 * live call fails closed when credentials are absent (mirrors Entra/QBO).
 */
// ---------------------------------------------------------------------------
// Connection
// ---------------------------------------------------------------------------
export const CW_SYNC_OUTCOMES = ['ok', 'partial', 'failed', 'running'];
// ---------------------------------------------------------------------------
// Imported time entries (staging mirror)
// ---------------------------------------------------------------------------
/** Billable disposition mirrored from CW's `billableOption`. */
export const CW_BILLABLE_OPTIONS = ['Billable', 'DoNotBill', 'NoCharge'];
/**
 * Whether a recorded sync entry was for one record, the whole dataset run, or a
 * non-fatal warning (the record synced, but part of it was skipped).
 */
export const CW_SYNC_ERROR_SCOPES = ['record', 'sync', 'warning'];
/**
 * How a sync run was triggered, derived from the audit entity id suffix:
 *  - `sync`   — a scheduled or manual full dataset sync.
 *  - `test`   — a per-record "Test" probe (sample or by-id).
 *  - `import` — an "Import all" run for a project/agreement.
 */
export const CW_SYNC_ACTIVITY_KINDS = ['sync', 'test', 'import'];
export const CW_SYNC_ACTIVITY_KIND_LABELS = {
    sync: 'Sync',
    test: 'Test',
    import: 'Import all',
};
// ---------------------------------------------------------------------------
// Datasets, field mapping & scheduling
// ---------------------------------------------------------------------------
/**
 * Read-only datasets that can be mirrored from ConnectWise into Catalyst. Unlike the
 * time-entry mirror, each dataset lands **directly in its native table**, tagged with
 * provenance (`source = 'connectwise'`, `cwId = <CW id>`) so records can be linked
 * back to their origin and distinguished from natively-created ones.
 */
export const CW_DATASETS = [
    'customers',
    'contacts',
    'projects',
    'agreements',
    'invoices',
    'products',
    'opportunities',
    'tickets',
    'timeEntries',
];
export const CW_DATASET_LABELS = {
    customers: 'Customers',
    contacts: 'Contacts',
    projects: 'Projects & phases',
    agreements: 'Agreements',
    invoices: 'Invoices',
    products: 'Products',
    opportunities: 'Opportunities',
    tickets: 'Tickets',
    timeEntries: 'Time entries',
};
/** Provenance marker stored on native records mirrored from an external source. */
export const CW_RECORD_SOURCES = ['native', 'connectwise'];
/** Datasets with a working field-mapping + sync implementation today. */
export const CW_IMPLEMENTED_DATASETS = [
    'customers',
    'contacts',
    'projects',
    'products',
    'agreements',
    'opportunities',
    'tickets',
    'timeEntries',
    'invoices',
];
/** Minimum cadence (minutes) allowed for the sequential dataset sync schedule. */
export const CW_SEQUENTIAL_MIN_INTERVAL_MINUTES = 5;
/**
 * Catalog of known CW source fields per dataset (offered as suggestions in the
 * mapping UI). The remote field accepts any dotted path, so this list is hints only.
 */
export const CW_SOURCE_FIELDS = {
    customers: [
        { key: 'identifier', label: 'Identifier' },
        { key: 'name', label: 'Company name' },
        { key: 'status.name', label: 'Status' },
        { key: 'phoneNumber', label: 'Phone' },
        { key: 'website', label: 'Website' },
        { key: 'addressLine1', label: 'Address line 1' },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'zip', label: 'Zip' },
        { key: 'billingTerms.name', label: 'Billing terms (finance)' },
        { key: 'defaultContact.name', label: 'Default contact name' },
    ],
    contacts: [
        { key: 'id', label: 'Contact id' },
        { key: 'firstName', label: 'First name' },
        { key: 'lastName', label: 'Last name' },
        { key: 'title', label: 'Title' },
        { key: 'communicationItems.Email', label: 'Email' },
        { key: 'communicationItems.Phone', label: 'Phone' },
        { key: 'company.id', label: 'Company id' },
        { key: 'company.identifier', label: 'Company identifier' },
        { key: 'company.name', label: 'Company name' },
        { key: 'inactiveFlag', label: 'Inactive flag' },
    ],
    projects: [
        { key: 'id', label: 'Project id' },
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' },
        { key: 'status.name', label: 'Status' },
        { key: 'type.name', label: 'Project type' },
        { key: 'company.id', label: 'Company id' },
        { key: 'company.identifier', label: 'Company identifier' },
        { key: 'company.name', label: 'Company name' },
        { key: 'manager.identifier', label: 'Manager identifier' },
        { key: 'manager.name', label: 'Manager name' },
        { key: 'estimatedStart', label: 'Estimated start' },
        { key: 'estimatedEnd', label: 'Estimated end' },
        { key: 'billProjectAfterClosedFlag', label: 'Bill after closed' },
    ],
    agreements: [
        { key: 'id', label: 'Agreement id' },
        { key: 'name', label: 'Name' },
        { key: 'type.name', label: 'Agreement type' },
        { key: 'agreementStatus', label: 'Status' },
        { key: 'company.id', label: 'Company id' },
        { key: 'company.identifier', label: 'Company identifier' },
        { key: 'company.name', label: 'Company name' },
        { key: 'startDate', label: 'Start date' },
        { key: 'endDate', label: 'End date' },
        { key: 'noEndingDateFlag', label: 'No ending date' },
        { key: 'cancelledFlag', label: 'Cancelled' },
        { key: 'customerPO', label: 'Customer PO' },
    ],
    invoices: [],
    products: [
        { key: 'identifier', label: 'Identifier (SKU)' },
        { key: 'description', label: 'Description (name)' },
        { key: 'customerDescription', label: 'Customer description' },
        { key: 'price', label: 'Price' },
        { key: 'cost', label: 'Cost' },
        { key: 'category.name', label: 'Category' },
        { key: 'subcategory.name', label: 'Subcategory' },
        { key: 'type.name', label: 'Type' },
        { key: 'productClass', label: 'Product class' },
        { key: 'manufacturerPartNumber', label: 'Manufacturer part number' },
        { key: 'inactiveFlag', label: 'Inactive flag' },
    ],
    opportunities: [
        { key: 'id', label: 'Opportunity id' },
        { key: 'name', label: 'Name' },
        { key: 'expectedCloseDate', label: 'Expected close date' },
        { key: 'type.name', label: 'Type' },
        { key: 'stage.name', label: 'Stage' },
        { key: 'status.name', label: 'Status' },
        { key: 'primarySalesRep.name', label: 'Primary sales rep' },
        { key: 'primarySalesRep.identifier', label: 'Primary sales rep identifier' },
        { key: 'company.id', label: 'Company id' },
        { key: 'company.identifier', label: 'Company identifier' },
        { key: 'company.name', label: 'Company name' },
        { key: 'contact.id', label: 'Contact id' },
        { key: 'contact.name', label: 'Contact name' },
        { key: 'notes', label: 'Notes' },
        { key: 'customFields.67', label: 'Custom: Length of Project' },
        { key: 'customFields.68', label: 'Custom: Length of Agreement' },
        { key: 'customFields.74', label: 'Custom: Marketing Acquisition' },
    ],
    tickets: [
        { key: 'summary', label: 'Summary (title)' },
        { key: 'status.name', label: 'Status' },
        { key: 'board.name', label: 'Board' },
        { key: 'priority.name', label: 'Priority' },
        { key: 'company.id', label: 'Company id' },
        { key: 'company.identifier', label: 'Company identifier' },
        { key: 'company.name', label: 'Company name' },
        { key: 'project.id', label: 'Project id' },
        { key: 'phase.id', label: 'Phase id' },
        { key: 'agreement.id', label: 'Agreement id' },
        { key: 'contact.id', label: 'Contact id' },
        { key: 'contact.name', label: 'Contact name' },
        { key: 'budgetHours', label: 'Budget hours' },
        { key: 'initialDescription', label: 'Initial description' },
    ],
    timeEntries: [
        { key: 'id', label: 'Time entry id' },
        { key: 'member.identifier', label: 'Member identifier' },
        { key: 'company.identifier', label: 'Company identifier' },
        { key: 'project.identifier', label: 'Project identifier' },
        { key: 'ticket.id', label: 'Ticket id' },
        { key: 'workType.name', label: 'Work type' },
        { key: 'timeStart', label: 'Time start' },
        { key: 'dateEntered', label: 'Date entered' },
        { key: 'actualHours', label: 'Actual hours' },
        { key: 'hoursBilled', label: 'Hours billed' },
        { key: 'billableOption', label: 'Billable option' },
        { key: 'invoice.identifier', label: 'CW invoice number (billed externally)' },
        { key: 'status', label: 'Status (written off)' },
        { key: 'notes', label: 'Notes' },
    ],
};
/** Default field mappings applied when a dataset has none configured yet. */
export const CW_DEFAULT_MAPPINGS = {
    customers: [
        { targetField: 'name', sourceField: 'name' },
        { targetField: 'netTerms', sourceField: 'billingTerms.name' },
    ],
    contacts: [
        { targetField: 'firstName', sourceField: 'firstName' },
        { targetField: 'lastName', sourceField: 'lastName' },
        { targetField: 'title', sourceField: 'title' },
        { targetField: 'email', sourceField: 'communicationItems.Email' },
        { targetField: 'phone', sourceField: 'communicationItems.Phone' },
    ],
    projects: [
        { targetField: 'name', sourceField: 'name' },
        { targetField: 'description', sourceField: 'description' },
        { targetField: 'startDate', sourceField: 'estimatedStart' },
        { targetField: 'endDate', sourceField: 'estimatedEnd' },
    ],
    agreements: [
        { targetField: 'name', sourceField: 'name' },
        { targetField: 'startDate', sourceField: 'startDate' },
        { targetField: 'endDate', sourceField: 'endDate' },
        { targetField: 'customerPo', sourceField: 'customerPO' },
    ],
    invoices: [],
    products: [
        { targetField: 'sku', sourceField: 'identifier' },
        { targetField: 'name', sourceField: 'description' },
        { targetField: 'customerDescription', sourceField: 'customerDescription' },
        { targetField: 'unitPrice', sourceField: 'price' },
    ],
    opportunities: [
        { targetField: 'name', sourceField: 'name' },
        { targetField: 'closeDate', sourceField: 'expectedCloseDate' },
        { targetField: 'lengthOfProject', sourceField: 'customFields.67' },
        { targetField: 'lengthOfAgreement', sourceField: 'customFields.68' },
        { targetField: 'marketingAcquisition', sourceField: 'customFields.74' },
    ],
    tickets: [
        { targetField: 'title', sourceField: 'summary' },
        { targetField: 'description', sourceField: 'initialDescription' },
        { targetField: 'budgetHours', sourceField: 'budgetHours' },
    ],
    timeEntries: [
        { targetField: 'workDate', sourceField: 'timeStart' },
        { targetField: 'hours', sourceField: 'actualHours' },
        { targetField: 'notes', sourceField: 'notes' },
    ],
};
//# sourceMappingURL=connectwise.js.map